import mpegts from 'mpegts.js'

type MediaPlayerSetup = (video: HTMLVideoElement) => { destroy?: () => void }

export type BufferedDurationCallback = (videoDurationInSeconds: number) => void

/**
 * Shared logic: Handles all timeouts, cleanup mechanics, abort hooks, and timeline resolution.
 */
function fetchMediaDuration(
  setupPlayer: MediaPlayerSetup,
  signal: AbortSignal,
  onBufferedDuration?: BufferedDurationCallback
): Promise<number | null> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      return reject(new DOMException('Aborted', 'AbortError'))
    }

    const video: HTMLVideoElement = document.createElement('video')
    video.muted = true

    const { destroy } = setupPlayer(video)

    const safetyTimeout = setTimeout(() => {
      cleanup()
      resolve(null)
    }, 5000)

    function cleanup(): void {
      clearTimeout(safetyTimeout)
      signal?.removeEventListener('abort', onAbort)

      if (destroy) {
        destroy()
      }

      video.removeAttribute('src')
      video.load() // Forces instant socket connection teardown
    }

    function onAbort(): void {
      cleanup()
      reject(new DOMException('Aborted', 'AbortError'))
    }

    if (signal) signal.addEventListener('abort', onAbort)

    // Track buffering progress percentages
    video.addEventListener('progress', () => {
      if (onBufferedDuration && video.buffered.length > 0) {
        // Get the absolute furthest timestamp in seconds currently downloaded
        const bufferedEnd = video.buffered.end(video.buffered.length - 1)
        onBufferedDuration(bufferedEnd)
      }
    })

    // Dynamic extraction parser checks
    const resolveDuration = (): void => {
      const duration = video.duration
      if (Number.isFinite(duration) && duration > 0) {
        onBufferedDuration?.(duration)
        resolve(duration)
        cleanup()
      }
    }

    video.addEventListener('loadedmetadata', () => {
      resolveDuration()
      // If the timeline remains broken/Infinity, skip to 21 minutes. Specialists can only ever
      // see a max of 20min for a whole engagement so this is a safe skip point
      if (!Number.isFinite(video.duration) || video.duration <= 0) {
        video.currentTime = 21 * 60 // 21 minutes in seconds
      }
    })

    video.addEventListener('durationchange', resolveDuration)
    video.addEventListener('error', () => {
      resolve(null)
      cleanup()
    })
  })
}

export function getFlvVideoDuration(
  flvUrl: string,
  token: string,
  signal: AbortSignal,
  onBufferedDuration?: BufferedDurationCallback
): Promise<number | null> {
  return fetchMediaDuration(
    video => {
      const config: mpegts.MediaDataSource = {
        type: 'flv',
        cors: true,
        hasAudio: true,
        hasVideo: true,
        url: flvUrl,
        isLive: false
      }
      let player: mpegts.Player | null = mpegts.createPlayer(config, {
        enableWorker: false,
        lazyLoad: true,
        autoCleanupSourceBuffer: true,
        headers: {
          Authorization: `Bearer ${token}`
        }
      })

      player.attachMediaElement(video)
      player.load()

      return {
        destroy: () => {
          if (player) {
            player.unload()
            player.detachMediaElement()
            player.destroy()
            player = null
          }
        }
      }
    },
    signal,
    onBufferedDuration
  )
}

export function getNativeVideoDuration(videoUrl: string, signal: AbortSignal, onBufferedDuration?: BufferedDurationCallback): Promise<number | null> {
  return fetchMediaDuration(
    video => {
      video.preload = 'metadata'
      video.src = videoUrl

      return {} // No third party background library engine to teardown
    },
    signal,
    onBufferedDuration
  )
}
